import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { FileText, Package, Clock, CheckCircle, ArrowRight, Plus, Megaphone, AlertTriangle, Info, AlertCircle, X, ChevronLeft, ChevronRight, Users, Globe, Map, Wallet, Receipt, Banknote, TrendingUp, Percent, Download, CalendarCheck2, Lock, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import CurrencyRates from "../../components/CurrencyRates";
import TravelNews from "../../components/TravelNews";
import FeaturedPackages from "../../components/FeaturedPackages";
import { toast } from "sonner";
import generateQuotePdfDoc from "../../utils/quotePdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const paxLabel = (b) => {
  const a = Number(b.pax_adults || 0);
  const c = Number(b.pax_children || 0);
  return c > 0 ? `${a}A + ${c}C` : `${a}A`;
};

const AgentDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(`dismissed_announcements_${user?.id}`) || "[]");
    } catch { return []; }
  });
  const [loading, setLoading] = useState(true);
  const [banners, setBanners] = useState([]);
  const [currentBanner, setCurrentBanner] = useState(0);
  const bannerTimer = useRef(null);
  const [liveRates, setLiveRates] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState("all");
  const [dashStats, setDashStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchDashStats = useCallback(async (period) => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/agent/dashboard-stats?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashStats(res.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [token]);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/banners/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(res.data);
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      const [quotesRes, bookingsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/announcements`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setQuotes(quotesRes.data);
      setBookings(bookingsRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    fetchBanners();
    fetchDashStats(statsPeriod);
    const fetchLiveRates = async () => {
      try {
        const res = await axios.get(`${API}/currency-rates`, { headers: { Authorization: `Bearer ${token}` } });
        setLiveRates(res.data);
      } catch {}
    };
    fetchLiveRates();
  }, [fetchData, fetchBanners, fetchDashStats, statsPeriod]);

  // Auto-rotate banners every 5s
  useEffect(() => {
    if (banners.length <= 1) return;
    bannerTimer.current = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(bannerTimer.current);
  }, [banners.length]);

  const goToBanner = useCallback((index) => {
    setCurrentBanner(index);
    clearInterval(bannerTimer.current);
    bannerTimer.current = setInterval(() => {
      setCurrentBanner(prev => (prev + 1) % banners.length);
    }, 5000);
  }, [banners.length]);

  const prevBanner = () => goToBanner((currentBanner - 1 + banners.length) % banners.length);
  const nextBanner = () => goToBanner((currentBanner + 1) % banners.length);

  const getUpcomingBookings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setDate(today.getDate() + 45);
    // Show until LAST day of travel: in-progress (return_date >= today) OR
    // upcoming (travel_date in [today, today+45]).
    return bookings.filter(b => {
        if (!b.travel_date) return false;
        if ((b.status || '').toLowerCase() === 'cancelled') return false;
        const tDate = new Date(b.travel_date); tDate.setHours(0,0,0,0);
        const rStr = b.return_date || b.travel_date;
        const rDate = new Date(rStr); rDate.setHours(0,0,0,0);
        const inProgress = tDate <= today && rDate >= today;
        const upcoming = tDate >= today && tDate <= limit;
        return inProgress || upcoming;
    }).sort((a, b) => new Date(a.travel_date) - new Date(b.travel_date));
  };

  const getCompletedBookings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return bookings.filter(b => {
        if ((b.status || '').toLowerCase() === 'cancelled') return false;
        const rStr = b.return_date || b.travel_date;
        if (!rStr) return false;
        const rDate = new Date(rStr); rDate.setHours(0,0,0,0);
        return rDate < today;
    }).sort((a, b) => new Date(b.return_date || b.travel_date) - new Date(a.return_date || a.travel_date)).slice(0, 10);
  };

  const getRecentQuotes = () => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    return quotes
      .filter(q => q.created_at && new Date(q.created_at) >= cutoff)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const upcomingBookings = getUpcomingBookings();
  const completedBookings = getCompletedBookings();
  const recentQuotes = getRecentQuotes();
  const [downloadingVoucher, setDownloadingVoucher] = useState(null);

  const handleDownloadVoucher = async (bookingId) => {
    setDownloadingVoucher(bookingId);
    try {
      // Use the SAME rich-itinerary PDF that admin gets in Booking Details.
      // Branding comes from this agent's profile.
      const [bookingRes, profileRes] = await Promise.all([
        axios.get(`${API}/bookings/${bookingId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      const booking = bookingRes.data;
      const me = profileRes.data || {};
      const advisor = {
        name: me.name || '',
        email: me.email || '',
        phone: me.phone || '',
        company_name: me.company_name || 'Happy Holidays DMC',
        designation: me.designation || '',
        address: me.address || '',
        city: me.city || '',
        state: me.state || '',
        website: me.website || '',
        logo_url: me.logo_url || '',
        profile_picture_url: me.profile_picture_url || '',
      };
      const quoteId = booking.quote_id;
      if (!quoteId) { toast.error("No linked quotation found"); return; }
      const quoteRes = await axios.get(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` } });
      let q = quoteRes.data;
      // Reflect any booking-level hotel/flight edits in the PDF.
      if (Array.isArray(booking.hotels_override) && booking.hotels_override.length > 0) {
        q = {
          ...q,
          hotels: booking.hotels_override,
          accommodation_options: (q.accommodation_options && q.accommodation_options.length > 0)
            ? [{ ...q.accommodation_options[0], hotels: booking.hotels_override }, ...q.accommodation_options.slice(1)]
            : [{ hotels: booking.hotels_override }],
        };
      }
      if (Array.isArray(booking.flights_override) && booking.flights_override.length > 0) {
        q = { ...q, flights: booking.flights_override };
      }
      const doc = generateQuotePdfDoc(q, advisor, {
        bookingConfirmations: booking.confirmations || [],
        agentView: true,
        bookingMode: true,
        bookingNumber: booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase(),
      });
      if (!doc) { toast.error("Could not generate voucher"); return; }
      const bno = booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase();
      doc.save(`Booking_${bno}.pdf`);
      toast.success("Voucher downloaded!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download voucher");
    } finally {
      setDownloadingVoucher(null);
    }
  };

  // Top KPI strip (Total Quotes / Pending / Approved / Total Bookings / Conversion %)
  const conversionPct = quotes.length > 0 ? ((bookings.length / quotes.length) * 100).toFixed(1) : "0.0";
  const stats = [
    { label: "Total Quotes", value: quotes.length, icon: FileText, color: "text-blue-600", bg: "bg-blue-50", to: "/agent/quotes" },
    { label: "Pending Quotes", value: quotes.filter(q => q.status === "pending").length, icon: Clock, color: "text-amber-600", bg: "bg-amber-50", to: "/agent/quotes?status=pending" },
    { label: "Approved Quotes", value: quotes.filter(q => q.status === "approved").length, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", to: "/agent/quotes?status=approved" },
    { label: "Total Bookings", value: bookings.length, icon: Package, color: "text-purple-600", bg: "bg-purple-50", to: "/agent/bookings" },
    { label: "Conversion %", value: `${conversionPct}%`, icon: Percent, color: "text-fuchsia-600", bg: "bg-fuchsia-50", to: "/agent/bookings" },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700 border-amber-200",
      confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
      cancelled: "bg-red-100 text-red-700 border-red-200",
      booked: "bg-blue-100 text-blue-700 border-blue-200",
      quoted: "bg-purple-100 text-purple-700 border-purple-200",
    };
    return styles[status] || "bg-gray-100 text-gray-700 border-gray-200";
  };

  const getPaymentBadge = (status) => {
    if (status === 'full') return "bg-emerald-100 text-emerald-700";
    if (status === 'partial') return "bg-orange-100 text-orange-700";
    return "bg-amber-100 text-amber-700";
  };

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-dashboard">
        {/* Welcome */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            {user?.logo_url && (
              <img
                src={user.logo_url}
                alt={user?.company_name || 'Agency Logo'}
                className="w-14 h-14 rounded-xl object-cover border border-gray-200 bg-white shadow-sm"
                data-testid="agent-dashboard-logo"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Welcome, {user?.name}!
              </h1>
              <p className="text-gray-500">
                {user?.company_name && `${user.company_name} • `}
                Agent Dashboard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {liveRates && (
              <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm" data-testid="inline-currency-rates">
                <span className="text-[10px] text-white bg-emerald-500 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wide">Live</span>
                {[
                  { key: "USD_INR", code: "USD", flag: "$" },
                  { key: "SGD_INR", code: "SGD", flag: "S$" },
                  { key: "THB_INR", code: "THB", flag: "\u0E3F" },
                ].map(({ key, code, flag }, idx) => (
                  <div key={key} className="flex items-center gap-1.5">
                    {idx > 0 && <span className="text-gray-200">|</span>}
                    <span className="text-xs text-gray-500">{flag} {code}</span>
                    <span className="text-sm font-bold text-gray-900 tabular-nums">₹{liveRates[key]?.toFixed(2) || '-'}</span>
                  </div>
                ))}
              </div>
            )}
            <Link to="/agent/request-quote">
              <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2 shadow-sm" data-testid="new-quote-btn">
                <Plus className="w-4 h-4" /> Request New Quote
              </Button>
            </Link>
          </div>
        </div>

        {/* Bookings Performance Snapshot */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-bookings-snapshot">
          <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Bookings Performance
            </CardTitle>
            <div className="flex items-center gap-2">
              <select
                value={statsPeriod}
                onChange={(e) => setStatsPeriod(e.target.value)}
                className="h-9 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="agent-stats-period"
              >
                <option value="all">All Time</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_fy">This Financial Year</option>
                <option value="last_fy">Last Financial Year</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const s = dashStats || {};
              const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
              const inr = (n) => `₹${fmt(n)}`;
              const paxStr = (() => {
                const a = s.pax_adults || 0;
                const c = s.pax_children || 0;
                return c > 0 ? `${a}A + ${c}C` : `${a}A`;
              })();
              const tiles = [
                { label: "Total Bookings", value: fmt(s.total_bookings), icon: Package, fg: "text-blue-700", bg: "bg-blue-50", testid: "stat-total-bookings" },
                { label: "Completed", value: fmt(s.completed_bookings), icon: CheckCircle, fg: "text-emerald-700", bg: "bg-emerald-50", testid: "stat-completed" },
                { label: "Total Pax", value: paxStr, icon: Users, fg: "text-violet-700", bg: "bg-violet-50", testid: "stat-pax" },
                { label: "Domestic", value: fmt(s.domestic), icon: Map, fg: "text-amber-700", bg: "bg-amber-50", testid: "stat-domestic" },
                { label: "International", value: fmt(s.international), icon: Globe, fg: "text-cyan-700", bg: "bg-cyan-50", testid: "stat-international" },
                { label: "Cancelled", value: fmt(s.cancelled_bookings), icon: X, fg: "text-rose-700", bg: "bg-rose-50", testid: "stat-cancelled" },
                { label: "Booking Amount", value: inr(s.total_booking_amount), icon: Receipt, fg: "text-indigo-700", bg: "bg-indigo-50", testid: "stat-booking-amount" },
                { label: "Paid Amount", value: inr(s.total_paid_amount), icon: Banknote, fg: "text-teal-700", bg: "bg-teal-50", testid: "stat-paid-amount" },
                { label: "Balance Amount", value: inr(s.total_balance_amount), icon: Wallet, fg: "text-rose-700", bg: "bg-rose-50", testid: "stat-balance-amount" },
                { label: "GST Amount", value: inr(s.total_gst_amount), icon: Receipt, fg: "text-amber-700", bg: "bg-amber-50", testid: "stat-gst-amount" },
                { label: "Profit Amount", value: inr(s.total_profit_amount), icon: TrendingUp, fg: "text-emerald-700", bg: "bg-emerald-50", testid: "stat-profit-amount" },
                { label: "Profit Margin", value: `${Number(s.profit_margin || 0).toFixed(2)}%`, icon: Percent, fg: "text-fuchsia-700", bg: "bg-fuchsia-50", testid: "stat-profit-margin" },
              ];
              if (statsLoading && !dashStats) {
                return <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-3">{Array.from({length:11}).map((_,i)=>(<div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />))}</div>;
              }
              return (
                <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-3">
                  {tiles.map((t, i) => (
                    <div key={i} className="rounded-xl border border-gray-100 bg-white px-4 py-3 hover:shadow-sm transition-all" data-testid={t.testid}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${t.bg} flex items-center justify-center shrink-0`}>
                          <t.icon className={`w-5 h-5 ${t.fg}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] text-gray-500 uppercase tracking-wide truncate">{t.label}</p>
                          <p className="font-bold text-gray-900 tabular-nums truncate text-base" title={t.value}>{t.value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Stats — Total Quotes / Pending / Approved / Total Bookings / Conversion % */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="agent-quick-stats">
          {stats.map((stat, index) => (
            <Card key={index} onClick={() => stat.to && navigate(stat.to)} className="bg-white border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" data-testid={`agent-dash-stat-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center shrink-0`}>
                    <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-gray-500 text-xs truncate">{stat.label}</p>
                    <p className="text-xl font-bold text-gray-900 tabular-nums">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Top 5 Domestic & International Destinations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="agent-top-destinations">
          {[
            { title: "Top 5 Domestic Destinations", list: dashStats?.top_domestic_destinations || [], chip: "bg-amber-50 text-amber-700", icon: Map, accent: "text-amber-600" },
            { title: "Top 5 International Destinations", list: dashStats?.top_international_destinations || [], chip: "bg-cyan-50 text-cyan-700", icon: Globe, accent: "text-cyan-600" },
          ].map((card, idx) => (
            <Card key={idx} className="bg-white border-gray-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <card.icon className={`w-5 h-5 ${card.accent}`} />
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {card.list.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No bookings in this period.</p>
                ) : (
                  <div className="overflow-x-auto" data-testid={idx === 0 ? "agent-top-domestic-list" : "agent-top-international-list"}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                          <th className="py-2 pr-2 font-semibold">#</th>
                          <th className="py-2 pr-2 font-semibold">Destination</th>
                          <th className="py-2 pr-2 font-semibold text-center">Bookings</th>
                          <th className="py-2 pr-2 font-semibold text-center">Pax</th>
                          <th className="py-2 font-semibold text-right">Booking Amt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {card.list.map((d, i) => {
                          const ba = Number(d.booking_amount || 0);
                          const adults = Number(d.pax_adults || 0);
                          const children = Number(d.pax_children || 0);
                          const fmtMoney = (n) => `${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                          return (
                            <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                              <td className="py-2 pr-2"><span className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-bold flex items-center justify-center tabular-nums">{i + 1}</span></td>
                              <td className="py-2 pr-2 font-medium text-gray-800">{d.destination}</td>
                              <td className="py-2 pr-2 text-center"><span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${card.chip}`}>{d.count}</span></td>
                              <td className="py-2 pr-2 text-center text-gray-700 tabular-nums whitespace-nowrap">{adults}A{children > 0 ? ` + ${children}C` : ''}</td>
                              <td className="py-2 text-right text-gray-800 tabular-nums">₹{fmtMoney(ba)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Banner Carousel */}
        {banners.length > 0 && (
          <div className="relative rounded-xl overflow-hidden shadow-md group" data-testid="banner-carousel">
            <div className="relative h-[200px] sm:h-[260px]">
              {banners.map((banner, index) => (
                <div
                  key={banner.id}
                  className={`absolute inset-0 transition-opacity duration-500 ${index === currentBanner ? "opacity-100 z-10" : "opacity-0 z-0"}`}
                >
                  {banner.link ? (
                    <a href={banner.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full cursor-pointer">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`} alt={banner.title || "Banner"} className="w-full h-full object-cover" />
                    </a>
                  ) : (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${banner.image_url}`} alt={banner.title || "Banner"} className="w-full h-full object-cover" />
                  )}
                  {banner.title && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-6 py-4">
                      <p className="text-white font-semibold text-lg">{banner.title}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Arrows */}
            {banners.length > 1 && (
              <>
                <button onClick={prevBanner} data-testid="banner-prev" className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button onClick={nextBanner} data-testid="banner-next" className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full bg-white/80 hover:bg-white shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </>
            )}
            {/* Dots */}
            {banners.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                {banners.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToBanner(i)}
                    data-testid={`banner-dot-${i}`}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentBanner ? "bg-white scale-110" : "bg-white/50 hover:bg-white/70"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Travel News */}
        <TravelNews />

        {/* Featured Packages */}
        <FeaturedPackages />

        {/* Announcements */}
        {announcements.filter(a => !dismissedIds.includes(a.id)).length > 0 && (
          <div className="space-y-3" data-testid="announcements-section">
            {announcements.filter(a => !dismissedIds.includes(a.id)).map(a => {
              const priorityCfg = {
                info: { bg: "bg-blue-50 border-blue-200", icon: Info, iconColor: "text-blue-500", badge: "bg-blue-100 text-blue-700" },
                important: { bg: "bg-amber-50 border-amber-200", icon: AlertTriangle, iconColor: "text-amber-500", badge: "bg-amber-100 text-amber-700" },
                urgent: { bg: "bg-red-50 border-red-200", icon: AlertCircle, iconColor: "text-red-500", badge: "bg-red-100 text-red-700" }
              };
              const cfg = priorityCfg[a.priority] || priorityCfg.info;
              const Icon = cfg.icon;
              return (
                <div
                  key={a.id}
                  className={`rounded-xl border p-4 ${cfg.bg} flex items-start gap-3`}
                  data-testid={`announcement-${a.id}`}
                >
                  <div className="mt-0.5">
                    <Megaphone className={`w-5 h-5 ${cfg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`${cfg.badge} text-xs`}>{a.priority?.toUpperCase()}</Badge>
                      <span className="text-xs text-gray-400">
                        {(() => { const d = new Date(a.created_at); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; })()}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-900 text-sm">{a.title}</h4>
                    <p className="text-gray-600 text-sm mt-0.5 whitespace-pre-line">{a.message}</p>
                  </div>
                  <button
                    onClick={() => {
                      const updated = [...dismissedIds, a.id];
                      setDismissedIds(updated);
                      localStorage.setItem(`dismissed_announcements_${user?.id}`, JSON.stringify(updated));
                    }}
                    className="flex-shrink-0 p-1 rounded-full hover:bg-black/10 transition-colors"
                    data-testid={`dismiss-announcement-${a.id}`}
                    title="Dismiss"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Recent Quotes */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-recent-quotes">
          <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
            <CardTitle className="text-gray-800 text-lg flex items-center gap-2">
              Recent Quotes <span className="text-sm font-normal text-gray-500">(Last 7 Days)</span>
            </CardTitle>
            <Link to="/agent/quotes">
              <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1 text-sm">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : recentQuotes.length === 0 ? (
              <div className="text-center py-10">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-4">No quotes in the last 7 days.</p>
                <Link to="/agent/request-quote">
                  <Button className="bg-emerald-600 hover:bg-emerald-700">Request Quote</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quote ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest Name / Pax</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination / Nights</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Travel Date / Return</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Est Cost</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentQuotes.map((quote) => {
                      const adults = quote.pax_adults || 0;
                      const children = quote.pax_children || 0;
                      const pax = children > 0 ? `${adults}A + ${children}C` : `${adults}A`;
                      return (
                      <tr key={quote.id} className="hover:bg-gray-50/50" data-testid={`recent-quote-row-${quote.id}`}>
                        <td className="px-4 py-3">
                          <Link to={`/agent/quotes/${quote.id}`} className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-xs hover:bg-emerald-100 hover:text-emerald-700 transition-colors">
                            #{quote.quote_number || quote.id.slice(0, 8).toUpperCase()}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-800">{quote.client_name || '-'}</p>
                          <p className="text-xs text-gray-500">{pax}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-gray-800">{quote.destination || '-'}</p>
                          <p className="text-xs text-gray-500">{quote.nights ? `${quote.nights}N` : '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-700">{formatDate(quote.travel_date)}</p>
                          <p className="text-xs text-gray-500">{formatDate(quote.return_date)}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-full border capitalize ${getStatusBadge(quote.status)}`}>
                            {quote.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                          {quote.final_total_amount ? `₹${quote.final_total_amount.toLocaleString()}` : quote.quote_amount ? `₹${quote.quote_amount.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Link to={`/agent/quotes/${quote.id}`}>
                            <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1 text-xs" data-testid={`view-recent-quote-${quote.id}`}>
                              <Eye className="w-3.5 h-3.5" /> View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Travel Bookings */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-upcoming-bookings">
          <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
            <CardTitle className="text-gray-800 text-lg flex items-center gap-2">
              <CalendarCheck2 className="w-5 h-5 text-emerald-600" />
              Upcoming Travel Bookings
            </CardTitle>
            <Link to="/agent/bookings">
              <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1 text-sm">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {upcomingBookings.length === 0 ? (
              <div className="text-center py-10">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No upcoming or in-progress trips.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Travel Date / Return</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest Name / Pax</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination / Nights</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {upcomingBookings.slice(0, 10).map((booking) => {
                      const pendingAmount = (booking.total_amount || 0) - (booking.amount_paid || 0);
                      const today = new Date(); today.setHours(0,0,0,0);
                      const tDate = new Date(booking.travel_date); tDate.setHours(0,0,0,0);
                      const inProgress = tDate <= today;
                      const bno = booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase();
                      const fullyPaid = booking.payment_status === 'full' || pendingAmount <= 0;
                      const canDownloadVoucher = !!booking.agent_download_enabled || fullyPaid;
                      return (
                        <tr key={booking.id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-3">
                            <Link
                              to={`/agent/bookings/${booking.id}`}
                              className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-xs hover:bg-emerald-100 hover:text-emerald-700 transition-colors"
                            >
                              #{bno}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-700">{formatDate(booking.travel_date)}</p>
                            <p className="text-xs text-gray-500">{formatDate(booking.return_date)}</p>
                            {inProgress && <span className="text-[10px] mt-0.5 text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded inline-block w-fit">In Progress</span>}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-800">{booking.guest_name || booking.client_name || '-'}</p>
                            <p className="text-xs text-gray-500">{paxLabel(booking)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-800">{booking.destination}</p>
                            <p className="text-xs text-gray-500">{booking.nights ? `${booking.nights}N` : '-'}</p>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">₹{(booking.total_amount || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-600">₹{(booking.amount_paid || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold">
                            <span className={pendingAmount > 0 ? "text-red-600" : "text-emerald-600"}>
                              ₹{pendingAmount.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {canDownloadVoucher ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={downloadingVoucher === booking.id}
                                onClick={() => handleDownloadVoucher(booking.id)}
                                className="text-emerald-700 hover:bg-emerald-50 text-xs gap-1"
                                data-testid={`download-voucher-${booking.id}`}
                              >
                                <Download className="w-3.5 h-3.5" />
                                {downloadingVoucher === booking.id ? '...' : 'Voucher'}
                              </Button>
                            ) : (
                              <span
                                className="inline-flex items-center text-[11px] text-gray-400 italic px-2 py-1 rounded"
                                title="Voucher unlocks after admin enables it or once full payment is received"
                                data-testid={`voucher-locked-${booking.id}`}
                              >
                                <Lock className="w-3 h-3 mr-1" /> Locked
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completed Tours */}
        <Card className="bg-white border-gray-200 shadow-sm" data-testid="agent-completed-tours">
          <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
            <CardTitle className="text-gray-800 text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-slate-500" />
              Completed Tours
            </CardTitle>
            <Link to="/agent/bookings">
              <Button variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 gap-1 text-sm">
                View All <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {completedBookings.length === 0 ? (
              <div className="text-center py-10">
                <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No completed tours yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed">
                  <thead className="bg-gray-50/80">
                    <tr>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Booking ID</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Travel Date / Return</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guest Name / Pax</th>
                      <th className="w-[12.5%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination / Nights</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Booking Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Amt</th>
                      <th className="w-[12.5%] px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {completedBookings.map((booking) => {
                      const bno = booking.booking_number || (booking.id || '').slice(0, 8).toUpperCase();
                      const pendingAmount = (booking.total_amount || 0) - (booking.amount_paid || 0);
                      return (
                        <tr key={booking.id} className="hover:bg-gray-50/50" data-testid={`completed-row-${booking.id}`}>
                          <td className="px-4 py-3">
                            <Link
                              to={`/agent/bookings/${booking.id}`}
                              className="font-mono text-slate-600 font-medium bg-slate-50 px-2 py-1 rounded text-xs hover:bg-slate-100 transition-colors"
                            >
                              #{bno}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-gray-700">{formatDate(booking.travel_date)}</p>
                            <p className="text-xs text-gray-500">{formatDate(booking.return_date)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-800">{booking.guest_name || booking.client_name || '-'}</p>
                            <p className="text-xs text-gray-500">{paxLabel(booking)}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-800">{booking.destination}</p>
                            <p className="text-xs text-gray-500">{booking.nights ? `${booking.nights}N` : '-'}</p>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">₹{(booking.total_amount || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-emerald-600">₹{(booking.amount_paid || 0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-sm font-semibold">
                            <span className={pendingAmount > 0 ? "text-red-600" : "text-emerald-600"}>
                              ₹{pendingAmount.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={downloadingVoucher === booking.id}
                              onClick={() => handleDownloadVoucher(booking.id)}
                              className="text-emerald-700 hover:bg-emerald-50 text-xs gap-1"
                              data-testid={`completed-download-voucher-${booking.id}`}
                            >
                              <Download className="w-3.5 h-3.5" />
                              {downloadingVoucher === booking.id ? '...' : 'Voucher'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AgentDashboard;
